import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type HTMLAttributes, type ReactElement, useState } from 'react'
import {
	ResponsiveConfirmDialog,
	ResponsiveConfirmTrigger
} from '@/features/marquee/components/responsive-confirm-dialog'
import { makeQueryKeys } from '@/features/marquee/query-keys'
import { errorHandler } from '@/helpers/utils'
import { closeEvent } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

interface CloseEventDialogProps {
	/** Event to close via POST /events/{id}/close. */
	eventId?: string
	/** Element that opens the confirmation (e.g. the "Close and export" button). */
	trigger: ReactElement<HTMLAttributes<HTMLElement>>
}

/**
 * "Close this event?" confirmation. Runs POST /events/{id}/close on confirm; the
 * Salesforce export runs server-side afterwards.
 */
function CloseEventDialog({ eventId, trigger }: CloseEventDialogProps) {
	const { t } = useTranslation()
	const [open, setOpen] = useState(false)
	const queryClient = useQueryClient()

	// Refreshes the detail + roster so the header badge and gating flip to
	// `closed`.
	const close = useMutation({
		mutationFn: () => closeEvent({ path: { eventId: eventId ?? '' } }),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.marquee.events.detail(eventId ?? '')
			})
			setOpen(false)
		},
		onError: (error) => errorHandler(error)
	})

	return (
		<ResponsiveConfirmDialog
			confirmLabel={t('marquee:checkin_close_event')}
			description={t('marquee:checkin_close_dialog_description')}
			isPending={close.isPending}
			onConfirm={() => close.mutate()}
			onOpenChange={setOpen}
			open={open}
			title={t('marquee:checkin_close_dialog_title')}
		>
			<ResponsiveConfirmTrigger>{trigger}</ResponsiveConfirmTrigger>
		</ResponsiveConfirmDialog>
	)
}

export { CloseEventDialog }
